/**
 * PlanUpsellTag — a normal in-flow row nudging the current plan toward the
 * next one up. Starter sees "Upgrade to Pro", Pro sees "Upgrade to Elite";
 * Elite sees nothing, because there's no plan above it to point at. Web
 * only — Guideline 3.1.1 keeps anything upgrade-shaped off native (see
 * UpgradePrompt.jsx, PlanGate.jsx for the same pattern elsewhere).
 *
 * Guests are skipped too: GuestPrompt in the sidebar already carries their
 * one persistent nudge (claim the account), and sending a guest to /pricing
 * before they have an account to attach a subscription to is a dead end.
 *
 * Deliberately NOT position:fixed. It used to float via
 * `top: calc(var(--safe-top) + Npx)`, which was silently broken on web the
 * entire time `--safe-top` had no :root fallback (see index.css) — an
 * undeclared custom property inside calc() is invalid at computed-value
 * time, which resets `top` to `auto`, so the offset never took effect no
 * matter what N was. Rendering as a normal flex row instead means its
 * position falls out of ordinary layout — inheriting AppShell's safe-area
 * padding for free — rather than depending on an offset calculation that
 * can silently go wrong again the same way. Callers render this as the
 * first child of their own flex container (see ChatPage.jsx) so it takes
 * its real height into account rather than floating over other content.
 */

import { useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { JAI_COLORS, JAI_FONTS } from './JaiBrand'
import { IS_NATIVE_APP } from '../services/runtime'

const NEXT_PLAN_NAME = { starter: 'Pro', pro: 'Elite' }

// Same diamond glyph as "Upgrade plan" in AccountMenu.jsx — one upgrade icon
// used consistently everywhere the app offers to upgrade.
const DiamondIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
    <path d="M5 3h14l3 5-10 13L2 8l3-5Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
    <path d="M2 8h20M8 3l-2 5 6 13M16 3l2 5-6 13"
      stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" opacity="0.7" />
  </svg>
)

export default function PlanUpsellTag() {
  const navigate = useNavigate()
  const { plan, planLoading, isGuest } = useAuth()

  if (IS_NATIVE_APP || planLoading || isGuest) return null

  const target = NEXT_PLAN_NAME[plan]
  if (!target) return null

  return (
    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
      <button
        type="button"
        onClick={() => navigate('/pricing')}
        title={`Upgrade to ${target}`}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          background: `linear-gradient(135deg, ${JAI_COLORS.blueBright}, ${JAI_COLORS.blue})`,
          color: 'white',
          border: 'none',
          borderRadius: 999,
          padding: '7px 14px',
          fontSize: 12.5,
          fontWeight: 700,
          fontFamily: JAI_FONTS.tight,
          letterSpacing: '-0.01em',
          cursor: 'pointer',
          boxShadow: '0 4px 14px rgba(30,91,214,0.35)',
          transition: 'filter 150ms, transform 150ms',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.filter = 'brightness(1.08)'; e.currentTarget.style.transform = 'translateY(-1px)' }}
        onMouseLeave={(e) => { e.currentTarget.style.filter = 'none'; e.currentTarget.style.transform = 'none' }}
      >
        <DiamondIcon />
        Upgrade to {target}
      </button>
    </div>
  )
}
